import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import {
    Eye, RefreshCw, X, Search, Plus,
    ClipboardList, Users, Building2, CalendarDays, Trash2, Save, FileText,
    Download, Sun, Moon
} from "lucide-react";

// ── Field accessors ──────────────────────────────────────────────────────────
const f = {
    id:           r => r.id            || r.Id            || 0,
    controlNo:    r => r.control_No    || r.Control_No    || r.controlNo    || "",
    documentCode: r => r.documentCode  || r.DocumentCode  || "",
    documentOwner:r => r.documentOwner || r.DocumentOwner || "",
    issueNo:      r => r.issueNo       || r.IssueNo       || "",
    revisionNo:   r => r.revisionNo    || r.RevisionNo    || "",
    revisionDate: r => r.revisionDate  || r.RevisionDate  || "",
    effectivityDate: r => r.effectivityDate || r.EffectivityDate || "",
    noOfPages:    r => r.noOfPages     || r.NoOfPages     || "",
    date:         r => r.date          || r.Date          || "",
    dayShift:     r => r.dayShift      || r.DayShift      || {},
    nightShift:   r => r.nightShift    || r.NightShift    || {},
    remarks:      r => r.remarks       || r.Remarks       || "",
    recordedBy:   r => r.recordedBy    || r.RecordedBy    || "",
    dateRecorded: r => r.dateRecorded  || r.DateRecorded  || "",
    createdBy:    r => r.createdBy     || r.CreatedBy     || "",
};

const PAGE_SIZE = 10;
const DRAFT_KEY = "production_scrap_draft";

// ── Scrap Codes (legend only — no abbr column needed) ───────────────────────
const SCRAP_CODES = [
    { code: "SCRP001", name: "DIAPER"                          },
    { code: "SCRP002", name: "NAPKIN"                          },
    { code: "SCRP003", name: "PANTYLINER"                      },
    { code: "SCRP050", name: "TUBE SMALL (1.5\"-7\")"          },
    { code: "SCRP051", name: "TUBE MEDIUM (8\"-13\")"          },
    { code: "SCRP051", name: "TUBE LONG WHITE"                 },
    { code: "SCRP052", name: "TUBE LONG BROWN (16.5\"-25.5\")" },
    { code: "SCRP053", name: "TUBE TAPE"                       },
    { code: "SCRP013", name: "FLUFF PULP"                      },
];

// ── Shift Group Codes — key is what's stored, label is what's shown ──────────
// Code column in each row is FIXED (the group number), NOT a dropdown.
// Tag No. is free-text input (e.g. "D-02584-A", "N-04085-A").
const SHIFT_GROUPS      = ["001", "002", "003", "TS_TM", "013"];
// Display label for the "Code" cell per group
const GROUP_CODE_LABEL  = { "001": "001", "002": "002", "003": "003", "TS_TM": "TS/TM", "013": "013" };

// code is no longer stored per-row — it's derived from the group key
const EMPTY_SHIFT_ENTRY = () => ({ time: "", tagNo: "", qty: "" });
const EMPTY_SHIFT_GROUP = () => ({
    "001":   Array.from({ length: 8 }, EMPTY_SHIFT_ENTRY),
    "002":   Array.from({ length: 8 }, EMPTY_SHIFT_ENTRY),
    "003":   Array.from({ length: 5 }, EMPTY_SHIFT_ENTRY),
    "TS_TM": Array.from({ length: 5 }, EMPTY_SHIFT_ENTRY),
    "013":   Array.from({ length: 3 }, EMPTY_SHIFT_ENTRY),
});

const EMPTY_FORM = () => ({
    controlNo:       "",
    documentCode:    "",
    documentOwner:   "",
    issueNo:         "",
    revisionNo:      "",
    revisionDate:    "",
    effectivityDate: "",
    noOfPages:       "",
    date:            new Date().toISOString().split("T")[0],
    dayShiftName:    "",
    nightShiftName:  "",
    dayShift:        EMPTY_SHIFT_GROUP(),
    nightShift:      EMPTY_SHIFT_GROUP(),
    remarks:         "",
    recordedBy:      "",
    dateRecorded:    new Date().toISOString().split("T")[0],
});

// ── Gradient header style ────────────────────────────────────────────────────
const gradHdr = { background: "linear-gradient(135deg, #0c1445 0%, #1a2f7a 50%, #1e40af 100%)" };

// ── PDF Export ───────────────────────────────────────────────────────────────
const exportPDF = async (data) => {
    if (!window.html2pdf) {
        await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
        });
    }
    const controlNo = data.control_No || data.controlNo || "export";

    const shiftTableHTML = (shiftData = {}, shiftName = "") => {
        const groups = ["001", "002", "003", "TS_TM", "013"];
        return groups.map(grp => {
            const rows = (shiftData[grp] || []).filter(r => r.time || r.code || r.tagNo || r.qty);
            if (!rows.length) return "";
            return `
            <div style="margin-bottom:6px">
                <div style="background:#1e3a8a;color:#fff;font-size:7px;font-weight:800;padding:3px 6px;text-transform:uppercase;letter-spacing:0.1em">
                    Code Group: ${grp}
                </div>
                <table style="width:100%;border-collapse:collapse;font-size:8px">
                    <tr style="background:#f1f5f9">
                        <th style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0;font-size:7px">Time</th>
                        <th style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0;font-size:7px">Code</th>
                        <th style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0;font-size:7px">Tag No.</th>
                        <th style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0;font-size:7px">Qty (Kgs.)</th>
                    </tr>
                    ${rows.map((r, i) => `
                    <tr style="background:${i%2===0?"#fff":"#f8fafc"}">
                        <td style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0">${r.time||""}</td>
                        <td style="padding:3px 5px;text-align:center;font-weight:700;color:#1e3a8a;border:0.5px solid #e2e8f0">${r.code||""}</td>
                        <td style="padding:3px 5px;text-align:center;border:0.5px solid #e2e8f0">${r.tagNo||""}</td>
                        <td style="padding:3px 5px;text-align:center;font-weight:600;border:0.5px solid #e2e8f0">${r.qty||""}</td>
                    </tr>`).join("")}
                </table>
            </div>`;
        }).join("");
    };

    const legendRows = SCRAP_CODES.map((sc, i) => `
        <tr>
            <td style="padding:2px 4px;font-size:7px;color:#64748b;width:14px">${i+1}</td>
            <td style="padding:2px 4px;font-size:7px;color:#334155"><span style="font-weight:700;color:#1e3a8a">${sc.code}</span> – ${sc.name}</td>
        </tr>`).join("");

    const html = `<div style="font-family:Arial,sans-serif;width:190mm;padding:6mm 8mm;background:#fff">
        <!-- HEADER -->
        <div style="background:linear-gradient(135deg,#0c1445,#1a2f7a,#1e40af);border-radius:8px;padding:10px 16px;display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
            <div style="display:flex;align-items:center;gap:8px">
                <div style="width:34px;height:34px;border-radius:50%;background:rgba(255,255,255,0.15);border:2px solid rgba(255,255,255,0.35);display:flex;align-items:center;justify-content:center">
                    <span style="color:#fff;font-size:8px;font-weight:900">MS</span>
                </div>
                <div>
                    <div style="color:#93c5fd;font-size:6.5px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase;margin-bottom:1px">Mega Soft Hygienic Products, Inc.</div>
                    <div style="color:#fff;font-size:13px;font-weight:900">Production Area Compacted Scrap</div>
                </div>
            </div>
            <div style="text-align:right;background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.2);border-radius:6px;padding:5px 12px">
                <div style="color:#93c5fd;font-size:6.5px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase">Control No.</div>
                <div style="color:#fff;font-size:14px;font-weight:900;letter-spacing:1px">${controlNo}</div>
            </div>
        </div>

        <!-- DOC META -->
        <div style="display:flex;gap:6px;margin-bottom:8px">
            <div style="flex:1;border:0.5px solid #e2e8f0;border-radius:5px;padding:5px 8px;background:#f8fafc;font-size:7px">
                <span style="color:#64748b;font-weight:700">Document Code: </span><span style="font-weight:700;color:#0f172a">${data.documentCode||""}</span>
                &nbsp;&nbsp;
                <span style="color:#64748b;font-weight:700">Document Owner: </span><span style="font-weight:700;color:#0f172a">${data.documentOwner||""}</span>
                &nbsp;&nbsp;
                <span style="color:#64748b;font-weight:700">Issue No.: </span><span style="font-weight:700;color:#0f172a">${data.issueNo||""}</span>
                &nbsp;&nbsp;
                <span style="color:#64748b;font-weight:700">Revision No.: </span><span style="font-weight:700;color:#0f172a">${data.revisionNo||""}</span>
                &nbsp;&nbsp;
                <span style="color:#64748b;font-weight:700">Effectivity Date: </span><span style="font-weight:700;color:#0f172a">${data.effectivityDate||""}</span>
            </div>
        </div>

        <!-- DAY + NIGHT COLUMNS -->
        <div style="display:flex;gap:8px;margin-bottom:8px">
            <div style="flex:1;border:0.5px solid #e2e8f0;border-radius:6px;overflow:hidden">
                <div style="background:#fef9c3;padding:5px 8px;font-size:8.5px;font-weight:800;color:#854d0e;display:flex;align-items:center;gap:4px;border-bottom:0.5px solid #fde68a">
                    ☀️ Day Shift — <span style="font-weight:700">${data.dayShiftName||""}</span>
                </div>
                <div style="padding:6px">${shiftTableHTML(data.dayShift||data.DayShift, "Day")}</div>
            </div>
            <div style="flex:1;border:0.5px solid #e2e8f0;border-radius:6px;overflow:hidden">
                <div style="background:#1e1b4b;padding:5px 8px;font-size:8.5px;font-weight:800;color:#c7d2fe;display:flex;align-items:center;gap:4px;border-bottom:0.5px solid #3730a3">
                    🌙 Night Shift — <span style="font-weight:700">${data.nightShiftName||""}</span>
                </div>
                <div style="padding:6px">${shiftTableHTML(data.nightShift||data.NightShift, "Night")}</div>
            </div>
        </div>

        <!-- LEGEND + REMARKS + SIGN -->
        <div style="display:flex;gap:8px">
            <div style="width:220px;flex-shrink:0;border:0.5px solid #e2e8f0;border-radius:6px;overflow:hidden">
                <div style="background:#f1f5f9;padding:4px 6px;font-size:7px;font-weight:800;color:#334155;text-transform:uppercase;letter-spacing:0.07em;border-bottom:0.5px solid #e2e8f0">
                    Scrap Code – Scrap Name Legend
                </div>
                <table style="width:100%;border-collapse:collapse">
                    <thead><tr style="background:#f8fafc">
                        <th style="padding:2px 4px;font-size:6.5px;color:#94a3b8;text-align:center;width:14px">#</th>
                        <th style="padding:2px 4px;font-size:6.5px;color:#94a3b8">Code – Name</th>
                    </tr></thead>
                    <tbody>${legendRows}</tbody>
                </table>
            </div>
            <div style="flex:1;border:0.5px solid #e2e8f0;border-radius:6px;padding:12px 16px;display:flex;flex-direction:column;gap:16px">
                <div>
                    <div style="font-size:7px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:6px">Remarks:</div>
                    <div style="font-size:9px;color:#0f172a;min-height:30px;border-bottom:1px solid #e2e8f0;padding-bottom:4px">${data.remarks||""}</div>
                </div>
                <div>
                    <div style="font-size:7px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:20px">Recorded By:</div>
                    <div style="font-size:12px;font-weight:700;color:#0f172a;border-bottom:1.5px solid #0f172a;padding-bottom:3px;min-width:130px;display:inline-block">${data.recordedBy||""}</div>
                    <div style="font-size:6.5px;color:#94a3b8;margin-top:3px;font-style:italic">Signature over Printed Name</div>
                </div>
                <div>
                    <div style="font-size:7px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px">Date Recorded:</div>
                    <div style="font-size:9px;font-weight:700;color:#0f172a">${data.dateRecorded||""}</div>
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <div style="margin-top:8px;padding-top:6px;border-top:0.5px solid #e2e8f0;text-align:center">
            <p style="font-size:6.5px;color:#94a3b8;font-style:italic;margin:0">
                This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
            </p>
        </div>
    </div>`;

    const el = document.createElement("div");
    el.innerHTML = html;
    document.body.appendChild(el);
    await window.html2pdf(el, {
        margin:      [4, 4, 4, 4],
        filename:    `ProductionScrap_${controlNo}.pdf`,
        image:       { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF:       { unit: "mm", format: "a4", orientation: "portrait" },
    });
    document.body.removeChild(el);
};

// ── Shift Entry Table ────────────────────────────────────────────────────────
// Code column = STATIC number (group code), NOT a dropdown.
// Tag No. = free-text input (e.g. "D-02584-A").
const ShiftGroupTable = ({ groupKey, entries, onChange, onAddRow, onRemoveRow }) => (
    <div className="mb-3">
        <div className="flex items-center justify-between px-3 py-1.5 rounded-t-lg" style={{ background: "#1e3a8a" }}>
            <span className="text-white text-[10px] font-bold uppercase tracking-widest">
                Code: <span className="text-blue-200">{GROUP_CODE_LABEL[groupKey]}</span>
            </span>
            <button onClick={onAddRow} className="flex items-center gap-1 px-2 py-0.5 rounded text-white text-[10px] font-bold cursor-pointer hover:bg-white/20 transition-colors"
                style={{ border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.1)" }}>
                <Plus size={10} /> Row
            </button>
        </div>
        <div className="border border-t-0 border-gray-200 rounded-b-lg overflow-hidden">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr className="bg-slate-50">
                        <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide text-center border-b border-gray-200 w-16">Time</th>
                        <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide text-center border-b border-gray-200 w-14">Code</th>
                        <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide text-center border-b border-gray-200">Tag No.</th>
                        <th className="px-2 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide text-center border-b border-gray-200 w-20">Qty (Kgs.)</th>
                        <th className="w-6 border-b border-gray-200" />
                    </tr>
                </thead>
                <tbody>
                    {entries.map((entry, idx) => (
                        <tr key={idx} className={`border-b border-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                            {/* Time */}
                            <td className="px-1.5 py-1">
                                <input type="time" value={entry.time} onChange={e => onChange(idx, "time", e.target.value)}
                                    className="w-full border border-gray-200 rounded px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
                            </td>
                            {/* Code — static display, NOT editable */}
                            <td className="px-1.5 py-1 text-center">
                                <span className="inline-block px-2 py-1 rounded text-[10px] font-extrabold text-blue-700 bg-blue-50 border border-blue-100 tracking-widest">
                                    {GROUP_CODE_LABEL[groupKey]}
                                </span>
                            </td>
                            {/* Tag No. — free text */}
                            <td className="px-1.5 py-1">
                                <input type="text" value={entry.tagNo} placeholder="e.g. D-02584-A"
                                    onChange={e => onChange(idx, "tagNo", e.target.value)}
                                    className="w-full border border-gray-200 rounded px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white font-mono" />
                            </td>
                            {/* Qty */}
                            <td className="px-1.5 py-1">
                                <input type="number" value={entry.qty} placeholder="0" min="0" step="0.01"
                                    onChange={e => onChange(idx, "qty", e.target.value)}
                                    className="w-full border border-gray-200 rounded px-1.5 py-1 text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white" />
                            </td>
                            {/* Remove */}
                            <td className="px-1 py-1 text-center">
                                {entries.length > 1 && (
                                    <button onClick={() => onRemoveRow(idx)} className="text-red-400 hover:text-red-600 cursor-pointer" style={{ border: "none", background: "transparent" }}>
                                        <Trash2 size={10} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// ── Document Preview Component ───────────────────────────────────────────────
const ProductionDocument = ({ data }) => {
    const controlNo = data.control_No || data.controlNo || "—";
    const docDate = data.date
        ? new Date(data.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
        : "";

    const ShiftView = ({ shiftData = {}, label, name, isDark }) => (
        <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden">
            <div className={`flex items-center gap-2 px-4 py-2.5 ${isDark ? "bg-indigo-950" : "bg-amber-50 border-b border-amber-100"}`}>
                {isDark ? <Moon size={13} className="text-indigo-300" /> : <Sun size={13} className="text-amber-500" />}
                <span className={`text-xs font-bold ${isDark ? "text-indigo-200" : "text-amber-800"}`}>{label}</span>
                {name && <span className={`text-xs font-extrabold ml-1 ${isDark ? "text-white" : "text-amber-900"}`}>— {name}</span>}
            </div>
            <div className="p-3">
                {SHIFT_GROUPS.map(grp => {
                    const rows = (shiftData[grp] || []).filter(r => r.time || r.tagNo || r.qty);
                    if (!rows.length) return null;
                    return (
                        <div key={grp} className="mb-3">
                            <div className="text-[9px] font-bold uppercase tracking-widest text-blue-800 bg-blue-50 px-2 py-1 rounded mb-1">Code: {GROUP_CODE_LABEL[grp]}</div>
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="bg-slate-50">
                                        {["Time", "Code", "Tag No.", "Qty (Kgs.)"].map(h => (
                                            <th key={h} className="px-2 py-1 text-[9px] font-bold text-slate-400 uppercase text-center border-b border-gray-100">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                                            <td className="px-2 py-1 text-center text-slate-600">{r.time || ""}</td>
                                            <td className="px-2 py-1 text-center font-bold text-blue-700">{GROUP_CODE_LABEL[grp]}</td>
                                            <td className="px-2 py-1 text-center text-slate-600 font-mono">{r.tagNo || ""}</td>
                                            <td className="px-2 py-1 text-center font-semibold text-slate-700">{r.qty || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
                {SHIFT_GROUPS.every(grp => !(shiftData[grp] || []).some(r => r.time || r.tagNo || r.qty)) && (
                    <p className="text-xs text-slate-400 italic text-center py-4">No entries</p>
                )}
            </div>
        </div>
    );

    return (
        <div style={{ fontFamily: "Arial, sans-serif" }}>
            {/* Header */}
            <div style={{ background: "linear-gradient(135deg,#0c1445 0%,#1a2f7a 50%,#1e40af 100%)", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "40px", height: "40px", borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.1)" }}>
                        <span style={{ color: "#fff", fontSize: "9px", fontWeight: "900" }}>MS</span>
                    </div>
                    <div>
                        <div style={{ color: "#93c5fd", fontSize: "8px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "2px" }}>Mega Soft Hygienic Products, Inc.</div>
                        <div style={{ color: "#fff", fontSize: "16px", fontWeight: "900" }}>Production Area Compacted Scrap</div>
                    </div>
                </div>
                <div style={{ textAlign: "right", background: "rgba(255,255,255,0.1)", border: "0.5px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "7px 14px" }}>
                    <div style={{ color: "#93c5fd", fontSize: "7.5px", fontWeight: "700", letterSpacing: "0.15em", textTransform: "uppercase" }}>Control No.</div>
                    <div style={{ color: "#fff", fontSize: "16px", fontWeight: "900", letterSpacing: "1.5px" }}>{controlNo}</div>
                </div>
            </div>

            {/* Doc meta bar */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0", padding: "8px 18px", background: "#f8fafc", borderBottom: "0.5px solid #e2e8f0" }}>
                {[
                    { label: "Date", value: docDate },
                    { label: "Document Code", value: data.documentCode || data.DocumentCode || "—" },
                    { label: "Issue No.", value: data.issueNo || data.IssueNo || "—" },
                    { label: "Effectivity Date", value: data.effectivityDate || data.EffectivityDate || "—" },
                ].map((m, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <span style={{ width: "1px", height: "14px", background: "#cbd5e1", display: "inline-block", margin: "0 12px" }} />}
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <span style={{ fontSize: "8px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>{m.label}:</span>
                            <span style={{ fontSize: "11px", fontWeight: "700", color: "#0f172a" }}>{m.value}</span>
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {/* Shift columns */}
            <div style={{ padding: "14px 16px 10px", display: "flex", gap: "12px" }}>
                <ShiftView shiftData={data.dayShift || data.DayShift || {}} label="Day Shift" name={data.dayShiftName || data.DayShiftName} isDark={false} />
                <ShiftView shiftData={data.nightShift || data.NightShift || {}} label="Night Shift" name={data.nightShiftName || data.NightShiftName} isDark={true} />
            </div>

            {/* Legend + Remarks + Recorded By */}
            <div style={{ display: "flex", gap: "12px", padding: "0 16px 14px" }}>
                {/* Legend — no abbr column, just #  +  Code-Name */}
                <div style={{ width: "220px", flexShrink: 0, border: "0.5px solid #e2e8f0", borderRadius: "6px", overflow: "hidden" }}>
                    <div style={{ background: "#f1f5f9", padding: "5px 8px", fontSize: "8px", fontWeight: "800", color: "#334155", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "0.5px solid #e2e8f0" }}>
                        Scrap Code – Scrap Name
                    </div>
                    {SCRAP_CODES.map((sc, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", padding: "3px 8px", borderBottom: "0.5px solid #f1f5f9" }}>
                            <span style={{ fontSize: "7.5px", color: "#94a3b8", width: "16px", flexShrink: 0 }}>{i + 1}</span>
                            <span style={{ fontSize: "7.5px", color: "#334155" }}>{sc.code} – {sc.name}</span>
                        </div>
                    ))}
                </div>

                {/* Remarks + Sign */}
                <div style={{ flex: 1, border: "0.5px solid #e2e8f0", borderRadius: "6px", padding: "16px 20px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                        <div style={{ fontSize: "8px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "6px" }}>Remarks:</div>
                        <div style={{ fontSize: "10px", color: "#0f172a", minHeight: "32px", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px" }}>
                            {data.remarks || data.Remarks || "\u00A0"}
                        </div>
                    </div>
                    <div style={{ marginTop: "16px" }}>
                        <div style={{ fontSize: "8px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "22px" }}>Recorded By:</div>
                        <div style={{ fontSize: "13px", fontWeight: "700", color: "#0f172a", borderBottom: "1.5px solid #0f172a", paddingBottom: "4px", display: "inline-block", minWidth: "140px" }}>
                            {data.recordedBy || data.RecordedBy || "\u00A0".repeat(20)}
                        </div>
                        <div style={{ fontSize: "7.5px", color: "#94a3b8", marginTop: "5px", fontStyle: "italic" }}>Signature over Printed Name</div>
                    </div>
                    <div style={{ marginTop: "14px" }}>
                        <div style={{ fontSize: "8px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px" }}>Date Recorded:</div>
                        <div style={{ fontSize: "11px", fontWeight: "700", color: "#0f172a" }}>{data.dateRecorded || data.DateRecorded || ""}</div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "8px 16px", borderTop: "0.5px solid #e2e8f0", background: "#f8fafc", textAlign: "center" }}>
                <p style={{ fontSize: "7.5px", color: "#94a3b8", fontStyle: "italic", margin: 0 }}>
                    This document is a property of Mega Soft Hygienic Products, Inc. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                </p>
            </div>
        </div>
    );
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ProductionScrap = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch {} }
    }, []);
    const userName = currentUser?.fullName || currentUser?.name || currentUser?.username || "Unknown User";

    const [records, setRecords]         = useState([]);
    const [loading, setLoading]         = useState(false);
    const [search, setSearch]           = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showForm, setShowForm]       = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [pdfLoading, setPdfLoading]   = useState(false);
    const [hasDraft, setHasDraft]       = useState(() => !!localStorage.getItem(DRAFT_KEY));
    const [form, setForm]               = useState(EMPTY_FORM());

    // ── Fetch ──────────────────────────────────────────────────────────────
    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ProductionScrap`);
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            setRecords(await res.json());
        } catch (err) {
            Swal.fire({ icon: "error", title: "Failed to load records", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    // ── Control No generator ──
    const generateControlNo = useCallback(() => {
        const now    = new Date();
        const yy     = String(now.getFullYear()).slice(-2);
        const mm     = String(now.getMonth() + 1).padStart(2, "0");
        const dd     = String(now.getDate()).padStart(2, "0");
        const prefix = `${yy}-${mm}${dd}-`;
        const maxSeq = records
            .map(r => f.controlNo(r))
            .filter(cn => cn.startsWith(prefix))
            .map(cn => parseInt(cn.replace(prefix, ""), 10))
            .filter(n => !isNaN(n))
            .reduce((max, n) => Math.max(max, n), 0);
        return `${prefix}${String(maxSeq + 1).padStart(5, "0")}`;
    }, [records]);

    // ── Draft helpers ──
    const saveDraft = () => {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setHasDraft(true);
        Swal.fire({ icon: "success", title: "Draft Saved!", timer: 1500, showConfirmButton: false });
    };
    const clearDraft = () => { localStorage.removeItem(DRAFT_KEY); setHasDraft(false); };

    const openForm = () => {
        setForm({ ...EMPTY_FORM(), controlNo: generateControlNo(), recordedBy: userName });
        setShowForm(true);
    };
    const openDraft = () => {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw);
            parsed.controlNo = generateControlNo(); // regenerate to avoid collision
            setForm(parsed);
        } catch {}
        setShowForm(true);
    };

    // ── Shift entry helpers ──
    const updateShiftEntry = (shift, grp, idx, field, value) => {
        setForm(prev => {
            const shiftData = { ...prev[shift] };
            const entries   = [...(shiftData[grp] || [])];
            entries[idx]    = { ...entries[idx], [field]: value };
            shiftData[grp]  = entries;
            return { ...prev, [shift]: shiftData };
        });
    };
    const addShiftRow = (shift, grp) => {
        setForm(prev => {
            const shiftData = { ...prev[shift] };
            shiftData[grp]  = [...(shiftData[grp] || []), EMPTY_SHIFT_ENTRY()];
            return { ...prev, [shift]: shiftData };
        });
    };
    const removeShiftRow = (shift, grp, idx) => {
        setForm(prev => {
            const shiftData = { ...prev[shift] };
            shiftData[grp]  = (shiftData[grp] || []).filter((_, i) => i !== idx);
            return { ...prev, [shift]: shiftData };
        });
    };

    // ── Submit ─────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!form.controlNo)
            return Swal.fire({ icon: "warning", title: "Control No. missing", confirmButtonColor: "#1d4ed8" });

        const hasEntries = SHIFT_GROUPS.some(grp =>
            [...(form.dayShift[grp] || []), ...(form.nightShift[grp] || [])].some(e => e.tagNo || e.qty)
        );
        if (!hasEntries)
            return Swal.fire({ icon: "warning", title: "No entries", text: "Please add at least one scrap entry.", confirmButtonColor: "#1d4ed8" });

        setFormLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/ProductionScrap`, {
                method:  "POST",
                headers: { "Content-Type": "application/json" },
                body:    JSON.stringify({
                    control_No:      form.controlNo,
                    documentCode:    form.documentCode,
                    documentOwner:   form.documentOwner,
                    issueNo:         form.issueNo,
                    revisionNo:      form.revisionNo,
                    revisionDate:    form.revisionDate,
                    effectivityDate: form.effectivityDate,
                    noOfPages:       form.noOfPages,
                    date:            form.date,
                    dayShiftName:    form.dayShiftName,
                    nightShiftName:  form.nightShiftName,
                    dayShift:        form.dayShift,
                    nightShift:      form.nightShift,
                    remarks:         form.remarks,
                    recordedBy:      form.recordedBy,
                    dateRecorded:    form.dateRecorded,
                    createdBy:       userName,
                }),
            });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.message || `Server error: ${res.status}`); }
            await Swal.fire({ icon: "success", title: "Saved!", text: "Production Scrap record created.", timer: 2000, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, {
                method:  "POST",
                headers: { "Content-Type": "application/json" },
                body:    JSON.stringify({ action: "CREATE", module: "Production Scrap", description: `Created Production Scrap #${form.controlNo}`, userId: currentUser?.id, username: currentUser?.username, fullName: userName }),
            }).catch(() => {});
            clearDraft(); setShowForm(false); fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Save Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setFormLoading(false); }
    };

    // ── Delete ─────────────────────────────────────────────────────────────
    const handleDelete = async (row) => {
        const id = f.id(row), ctrlNo = f.controlNo(row);
        const { isConfirmed } = await Swal.fire({
            icon: "warning", title: "Delete Record?",
            html: `Are you sure you want to delete <br/><strong>${ctrlNo}</strong>?<br/>This action cannot be undone.`,
            showCancelButton: true, confirmButtonText: "Yes, Delete", cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280",
        });
        if (!isConfirmed) return;
        try {
            const res = await fetch(`${API_BASE_URL}api/ProductionScrap/${id}`, { method: "DELETE" });
            if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e?.message || `Server error: ${res.status}`); }
            await Swal.fire({ icon: "success", title: "Deleted!", text: `Record ${ctrlNo} deleted.`, timer: 1800, showConfirmButton: false });
            fetch(`${API_BASE_URL}api/Logs`, {
                method:  "POST",
                headers: { "Content-Type": "application/json" },
                body:    JSON.stringify({ action: "DELETE", module: "Production Scrap", description: `Deleted Production Scrap #${ctrlNo}`, userId: currentUser?.id, username: currentUser?.username, fullName: userName }),
            }).catch(() => {});
            fetchRecords();
        } catch (err) {
            Swal.fire({ icon: "error", title: "Delete Failed", text: err.message, confirmButtonColor: "#dc2626" });
        }
    };

    // ── PDF Export ─────────────────────────────────────────────────────────
    const handleExportPDF = async (row) => {
        setPdfLoading(true);
        try { await exportPDF(row); }
        catch (err) { Swal.fire({ icon: "error", title: "PDF Export Failed", text: err.message, confirmButtonColor: "#dc2626" }); }
        finally { setPdfLoading(false); }
    };

    // ── Filter + paginate ──
    const filtered   = records.filter(row => {
        const q = search.toLowerCase();
        return !q || f.controlNo(row).toLowerCase().includes(q)
                  || f.documentCode(row).toLowerCase().includes(q)
                  || f.recordedBy(row).toLowerCase().includes(q);
    });
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const paginBtn   = (d) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${d ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* PAGE HEADER */}
                <div style={gradHdr} className="px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList className="text-white" size={22} /></div>
                        <div>
                            <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Production Area</p>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Production Area Compacted Scrap</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                            <Users size={14} className="text-white/70" />
                            <span className="text-white text-sm font-semibold">{userName}</span>
                        </div>
                        {hasDraft && (
                            <button onClick={openDraft} className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#92400e,#b45309)", boxShadow: "0 2px 10px rgba(180,83,9,0.40)" }}>
                                <FileText size={15} /> Restore Draft
                            </button>
                        )}
                        <button onClick={openForm} className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold border-0 cursor-pointer transition-all hover:-translate-y-0.5"
                            style={{ background: "linear-gradient(135deg,#15803d,#16a34a)", boxShadow: "0 2px 10px rgba(22,163,74,0.35)" }}>
                            <Plus size={16} /> New Record
                        </button>
                    </div>
                </div>

                <div className="p-8">
                    {/* TOOLBAR */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search control no, document code, recorded by..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>
                        <button onClick={fetchRecords} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {!loading && <p className="text-xs text-slate-500 mb-3">{filtered.length} record{filtered.length !== 1 ? "s" : ""} found</p>}

                    {/* TABLE */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading records...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">No records found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "Control No", "Date", "Document Code", "Effectivity Date", "Recorded By", "Actions"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Actions" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => (
                                        <tr key={f.id(row) || idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                            <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                            <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap">{f.controlNo(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.documentCode(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.effectivityDate(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.recordedBy(row) || "—"}</td>
                                            <td className="px-3 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button onClick={() => setSelectedRow(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                        <Eye size={13} /> View
                                                    </button>
                                                    <button onClick={() => handleDelete(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all cursor-pointer">
                                                        <Trash2 size={13} /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══════════════ NEW RECORD FORM MODAL ══════════════ */}
            {showForm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
                    <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        {/* Modal Header */}
                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em]">New Entry</p>
                                    <h2 className="text-white font-extrabold text-base">Production Area Compacted Scrap</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-right">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-widest">{form.controlNo}</p>
                                </div>
                                <button onClick={() => setShowForm(false)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center cursor-pointer transition-all" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-7 bg-slate-50">

                            {/* Document Information */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Document Information</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Control No.</label>
                                        <div className="w-full border-2 border-blue-100 bg-blue-50 rounded-xl px-3 py-2.5 text-sm font-extrabold text-blue-700 tracking-widest select-all">{form.controlNo}</div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Date</label>
                                        <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Document Code</label>
                                        <input type="text" value={form.documentCode} placeholder="e.g. MSHPI-SP-QP-014" onChange={e => setForm(p => ({ ...p, documentCode: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Document Owner</label>
                                        <input type="text" value={form.documentOwner} placeholder="Owner..." onChange={e => setForm(p => ({ ...p, documentOwner: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Issue No.</label>
                                        <input type="text" value={form.issueNo} placeholder="0" onChange={e => setForm(p => ({ ...p, issueNo: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Revision No.</label>
                                        <input type="text" value={form.revisionNo} placeholder="0" onChange={e => setForm(p => ({ ...p, revisionNo: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Effectivity Date</label>
                                        <input type="date" value={form.effectivityDate} onChange={e => setForm(p => ({ ...p, effectivityDate: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">No. of Pages</label>
                                        <input type="text" value={form.noOfPages} placeholder="1 of 1" onChange={e => setForm(p => ({ ...p, noOfPages: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Day Shift + Night Shift side by side */}
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5">
                                {/* DAY SHIFT */}
                                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-amber-100 bg-amber-50">
                                        <Sun size={16} className="text-amber-500" />
                                        <span className="text-sm font-bold text-amber-800">Day Shift</span>
                                        <div className="ml-auto">
                                            <input type="text" value={form.dayShiftName} placeholder="Operator name..." onChange={e => setForm(p => ({ ...p, dayShiftName: e.target.value }))}
                                                className="border border-amber-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white" />
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        {SHIFT_GROUPS.map(grp => (
                                            <ShiftGroupTable key={grp} groupKey={grp}
                                                entries={form.dayShift[grp] || []}
                                                onChange={(idx, field, val) => updateShiftEntry("dayShift", grp, idx, field, val)}
                                                onAddRow={() => addShiftRow("dayShift", grp)}
                                                onRemoveRow={(idx) => removeShiftRow("dayShift", grp, idx)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* NIGHT SHIFT */}
                                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-indigo-900 bg-indigo-950">
                                        <Moon size={16} className="text-indigo-300" />
                                        <span className="text-sm font-bold text-indigo-200">Night Shift</span>
                                        <div className="ml-auto">
                                            <input type="text" value={form.nightShiftName} placeholder="Operator name..." onChange={e => setForm(p => ({ ...p, nightShiftName: e.target.value }))}
                                                className="border border-indigo-700 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-indigo-900 text-white placeholder-indigo-400" />
                                        </div>
                                    </div>
                                    <div className="p-4">
                                        {SHIFT_GROUPS.map(grp => (
                                            <ShiftGroupTable key={grp} groupKey={grp}
                                                entries={form.nightShift[grp] || []}
                                                onChange={(idx, field, val) => updateShiftEntry("nightShift", grp, idx, field, val)}
                                                onAddRow={() => addShiftRow("nightShift", grp)}
                                                onRemoveRow={(idx) => removeShiftRow("nightShift", grp, idx)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Remarks + Recorded By + Date Recorded */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
                                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Closing Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-1">
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Remarks</label>
                                        <textarea value={form.remarks} rows={3} placeholder="e.g. GrAss# 91420" onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Recorded By</label>
                                        <input type="text" value={form.recordedBy} onChange={e => setForm(p => ({ ...p, recordedBy: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1.5">Date Recorded</label>
                                        <input type="date" value={form.dateRecorded} onChange={e => setForm(p => ({ ...p, dateRecorded: e.target.value }))}
                                            className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                                    </div>
                                </div>
                            </div>

                            {/* Scrap Code Reference */}
                            <div className="bg-white border border-gray-200 rounded-2xl p-4" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Scrap Code Reference</p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-0.5">
                                    {SCRAP_CODES.map((sc, i) => (
                                        <div key={sc.code + i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50">
                                            <span className="text-xs font-bold text-slate-400 w-5 text-right flex-shrink-0">{i + 1}.</span>
                                            <span className="text-xs text-slate-600"><span className="font-bold text-blue-700">{sc.code}</span> – {sc.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0" style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={saveDraft} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-amber-700 text-sm font-bold transition-all cursor-pointer hover:-translate-y-0.5" style={{ background: "#fef3c7", border: "2px solid #fde68a" }}>
                                <FileText size={14} /> Save Draft
                            </button>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setShowForm(false)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                    <X size={14} /> Cancel
                                </button>
                                <button onClick={handleSubmit} disabled={formLoading}
                                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-bold cursor-pointer disabled:opacity-50 transition-all hover:-translate-y-0.5"
                                    style={{ background: "linear-gradient(135deg,#1a2f7a,#1e40af)", boxShadow: "0 2px 10px rgba(30,64,175,0.35)", border: "none" }}>
                                    <Save size={15} />
                                    {formLoading ? "Saving..." : "Save Record"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════ VIEW MODAL ══════════════ */}
            {selectedRow && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>
                    <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40)" }}>

                        <div style={gradHdr} className="px-7 py-5 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/15 border border-white/20 p-2.5 rounded-2xl"><ClipboardList size={20} className="text-white" /></div>
                                <div>
                                    <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">Mega Soft · Production Area</p>
                                    <h2 className="text-white font-extrabold text-base leading-none">Production Area Compacted Scrap</h2>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                    <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                    <p className="text-white font-extrabold text-sm tracking-wider">{f.controlNo(selectedRow)}</p>
                                </div>
                                <button onClick={() => setSelectedRow(null)} className="bg-white/15 hover:bg-white/25 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer" style={{ border: "none" }}>
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Meta bar */}
                        <div className="px-7 py-2.5 flex items-center gap-1 flex-wrap flex-shrink-0" style={{ background: "rgba(12,20,69,0.93)" }}>
                            {[
                                { icon: CalendarDays, label: "Date",        value: f.date(selectedRow) ? new Date(f.date(selectedRow)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—" },
                                { icon: FileText,     label: "Doc Code",    value: f.documentCode(selectedRow) || "—" },
                                { icon: Building2,    label: "Recorded By", value: f.recordedBy(selectedRow) || "—" },
                            ].map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                        <span className="flex items-center gap-2 text-xs">
                                            <Icon size={12} className="text-blue-400" />
                                            <span className="text-white/50">{item.label}:</span>
                                            <span className="text-white font-semibold">{item.value}</span>
                                        </span>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Document preview */}
                        <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                            <div className="p-6">
                                <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                                    <ProductionDocument data={selectedRow} />
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between flex-shrink-0" style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>
                            <button onClick={() => handleExportPDF(selectedRow)} disabled={pdfLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all cursor-pointer hover:-translate-y-0.5"
                                style={{ background: "linear-gradient(135deg,#dc2626,#b91c1c)", boxShadow: "0 2px 10px rgba(220,38,38,0.35)", border: "none" }}>
                                <Download size={15} />
                                {pdfLoading ? "Generating PDF..." : "Export PDF"}
                            </button>
                            <button onClick={() => setSelectedRow(null)}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer">
                                <X size={14} /> Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionScrap;